import {Color} from '@benchling/nucleus-ui';
import {Divider} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import AntibodyComponentInputs, {DomainWithProtein} from 'antibodies/actions/AntibodyComponentInputs';
import {createAntibodies, CreateAntibodyApiInput} from 'antibodies/AntibodyDAO';
import {useImplementedAntibodySchemaInterfacesQuery} from 'antibodies/hooks/useImplementedAntibodySchemaInterfacesQuery';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {FONT_PROXIMA_12} from 'styles/FontConstants';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextInput from 'ui-library/inputs/TextInput';
import Modal from 'ui-library/modals/Modal';
import {CenteredSpinner} from 'ui-library/Spinner';

export interface AntibodyCreateModalProps {
  initialFolderId: string | null;
  initialSchemaId?: string | null;
  onRequestClose: () => void;
}

/**
 * Modal for creating an antibody entity.
 * @param initialFolderId The folder that the entity should be created in
 * @param initialSchemaId The API ID of the schema under which to create the entity
 * @param onRequestClose A function to be called when the modal is closed
 */
export default function AntibodyCreateModal({
  initialFolderId,
  initialSchemaId,
  onRequestClose,
}: AntibodyCreateModalProps): JSX.Element {
  const {status, implementedAntibodySchemaInterfaces} = useImplementedAntibodySchemaInterfacesQuery();
  const [creating, setCreating] = useState(false);
  const [folderId, setFolderId] = useState(
    initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY)
  );
  const [name, setName] = useState('');
  const [domainsWithProteinByLabel, setDomainsWithProteinByLabel] = useState<
    Record<string, DomainWithProtein>
  >({});
  const [schemaId, setSchemaId] = useState(initialSchemaId);

  const antibodySchemaIds = (implementedAntibodySchemaInterfaces ?? []).map(({schema: {id}}) => id);
  const selectedSchemaInterface = (implementedAntibodySchemaInterfaces ?? []).find(
    ({schema: {id}}) => id === schemaId
  );

  const sortedDistinctDomains = useMemo(
    () => (selectedSchemaInterface == null ? [] : selectedSchemaInterface.format.domains),
    [selectedSchemaInterface]
  );
  const canSubmit =
    status === QueryResultType.SUCCESS &&
    !creating &&
    name.length > 0 &&
    folderId != null &&
    schemaId != null &&
    sortedDistinctDomains.every((domain) => domainsWithProteinByLabel[domain.label]?.proteinId != null);

  const onRequestSubmit = async (): Promise<void> => {
    setCreating(true);
    const domains = Object.values(domainsWithProteinByLabel).map((domainWithProtein) => ({
      label: domainWithProtein.label,
      aaSequenceId: domainWithProtein.proteinId!,
    }));
    const apiInput: CreateAntibodyApiInput = {
      name,
      folderId: folderId!,
      schemaId: schemaId!,
      domains,
    };
    try {
      const {antibodyIds} = await createAntibodies([apiInput]);
      FolderDAOCache.invalidateFolderContents(folderId);
      FileBrowserActions.openFile(antibodyIds[0]);
      onRequestClose();
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      buttons={[{text: creating ? 'Creating...' : 'Create', submit: true, disabled: !canSubmit}]}
      expandHeight={true}
      large={selectedSchemaInterface != null}
      onRequestClose={onRequestClose}
      onRequestSubmit={onRequestSubmit}
      title="Create protein"
    >
      <Row>
        <Col sm={12}>
          <FormGroup>
            <FieldLabel label="Entity name" isRequired={true} />
            <TextInput
              autoFocus={true}
              disabled={creating}
              onChange={(e) => {
                setName(e.target.value);
              }}
              placeholder="Name"
              value={name}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <FormGroup>
            <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={true} />
            <FolderSelector
              enableRedesign={true}
              onChange={(e) => {
                setFolderId(e.target.value);
              }}
              readOnly={creating}
              unsetOnFetchFail={true}
              value={folderId}
              showInventory={true}
            />
          </FormGroup>
        </Col>
        <Col sm={6}>
          <FormGroup>
            <FieldLabel label="Schema" isRequired={true} />
            <TagSchemaSelector
              isDisabled={creating || implementedAntibodySchemaInterfaces.length === 0}
              oligoAwareFolderItemTypes={[ModelTypeConstants.BASIC_FOLDER_ITEM]}
              onChange={(tagSchema) => {
                setSchemaId(tagSchema!.id);
              }}
              selectedTagSchemaId={schemaId}
              tagSchemaFilterFn={(tagSchema) => antibodySchemaIds.includes(tagSchema.id)}
            />
          </FormGroup>
        </Col>
      </Row>
      {schemaId != null && selectedSchemaInterface != null && (
        <>
          <Divider my="md" />
          <div className={css(styles.sectionTitle)}>Format</div>
          <AntibodyComponentInputs
            antibodySchemaId={schemaId}
            disabled={creating}
            format={selectedSchemaInterface.format}
            domainsWithProteinByLabel={domainsWithProteinByLabel}
            setDomainsWithProteinByLabel={setDomainsWithProteinByLabel}
          />
        </>
      )}
      {schemaId != null && status === QueryResultType.PENDING && <CenteredSpinner />}
      {status === QueryResultType.ERROR && <span>Error loading protein schemas</span>}
    </Modal>
  );
}

const styles = StyleSheet.create({
  sectionTitle: {
    ...FONT_PROXIMA_12,
    color: Color.GRAY_600,
    fontWeight: 600,
    textTransform: 'uppercase',
    marginBottom: '16px',
  },
});
